const normalizeRotation = (rotation) =>
  ((Number(rotation) % 360) + 360) % 360;

export const getValidNormalizedPolygon = (sourceBbox) => {
  if (
    sourceBbox?.version !== 1 ||
    sourceBbox?.space !== "normalized_page_image" ||
    !Array.isArray(sourceBbox?.polygon) ||
    sourceBbox.polygon.length !== 8
  ) {
    return null;
  }
  const polygon = sourceBbox.polygon.map(Number);
  return polygon.every(
    (value) => Number.isFinite(value) && value >= 0 && value <= 1
  )
    ? polygon
    : null;
};

export const rotateNormalizedPolygon = (polygon, rotation = 0) => {
  if (!Array.isArray(polygon) || polygon.length !== 8) return null;
  const normalizedRotation = normalizeRotation(rotation);
  const rotated = [];
  for (let index = 0; index < polygon.length; index += 2) {
    const x = polygon[index];
    const y = polygon[index + 1];
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    if (normalizedRotation === 90) {
      rotated.push(1 - y, x);
    } else if (normalizedRotation === 180) {
      rotated.push(1 - x, 1 - y);
    } else if (normalizedRotation === 270) {
      rotated.push(y, 1 - x);
    } else {
      rotated.push(x, y);
    }
  }
  return rotated;
};

export const polygonToSvgPoints = (sourceBbox, rotation = 0) => {
  const polygon = getValidNormalizedPolygon(sourceBbox);
  const rotated = rotateNormalizedPolygon(polygon, rotation);
  if (!rotated) return null;
  const points = [];
  for (let index = 0; index < rotated.length; index += 2) {
    points.push(`${rotated[index]},${rotated[index + 1]}`);
  }
  return points.join(" ");
};

export const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");

const sharedPrefixLength = (a, b) => {
  const limit = Math.min(a.length, b.length);
  let index = 0;
  while (index < limit && a[index] === b[index]) index += 1;
  return index;
};

const collectSubstringMatches = (pageText, needle) => {
  if (!needle || needle.length < 3) return [];
  const matches = [];
  let searchFrom = 0;
  while (searchFrom <= pageText.length - needle.length) {
    const matchIndex = pageText.indexOf(needle, searchFrom);
    if (matchIndex === -1) break;
    matches.push(matchIndex);
    searchFrom = matchIndex + Math.max(needle.length, 1);
  }
  return matches;
};

/**
 * Map a form/OCR query onto pdf.js text spans.
 * Falls back to a shared prefix / long token when OCR drifts from PDF text
 * (e.g. "…Flasche à 2l" vs "…Flasche à 200 ml").
 */
export const findTextHighlightSpanIndexes = (
  textItems,
  query,
  occurrence = 0
) => {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length < 3) return [];

  let pageText = "";
  const characterToSpan = [];
  (textItems || []).forEach((item, spanIndex) => {
    const normalized = normalizeSearchText(item?.str);
    pageText += normalized;
    characterToSpan.push(...Array(normalized.length).fill(spanIndex));
  });

  let matches = collectSubstringMatches(pageText, normalizedQuery);
  let matchLength = normalizedQuery.length;

  if (!matches.length && normalizedQuery.length >= 8) {
    const minLength = Math.max(8, Math.floor(normalizedQuery.length * 0.55));
    for (let length = normalizedQuery.length - 1; length >= minLength; length -= 1) {
      const needle = normalizedQuery.slice(0, length);
      matches = collectSubstringMatches(pageText, needle);
      if (matches.length) {
        matchLength = length;
        break;
      }
    }
  }

  if (!matches.length) {
    const tokens = (normalizedQuery.match(/[\p{L}\p{N}]{6,}/gu) || []).sort(
      (a, b) => b.length - a.length
    );
    for (const token of tokens) {
      matches = collectSubstringMatches(pageText, token);
      if (matches.length) {
        matchLength = token.length;
        break;
      }
    }
  }

  const selected = matches[Math.max(0, Number(occurrence) || 0)];
  if (selected === undefined) return [];
  return Array.from(
    new Set(characterToSpan.slice(selected, selected + matchLength))
  );
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));

export const rectToNormalizedPolygon = (left, top, right, bottom) => {
  const l = clamp01(Number(left));
  const t = clamp01(Number(top));
  const r = clamp01(Number(right));
  const b = clamp01(Number(bottom));
  if (!(r > l) || !(b > t)) return null;
  return [l, t, r, t, r, b, l, b];
};

export const getPolygonBounds = (polygon) => {
  if (!Array.isArray(polygon) || polygon.length !== 8) return null;
  const xs = [polygon[0], polygon[2], polygon[4], polygon[6]].map(Number);
  const ys = [polygon[1], polygon[3], polygon[5], polygon[7]].map(Number);
  if (xs.some((v) => !Number.isFinite(v)) || ys.some((v) => !Number.isFinite(v))) {
    return null;
  }
  return {
    left: Math.min(...xs),
    top: Math.min(...ys),
    right: Math.max(...xs),
    bottom: Math.max(...ys),
  };
};

export const polygonArea = (polygon) => {
  const bounds = getPolygonBounds(polygon);
  if (!bounds) return Number.POSITIVE_INFINITY;
  return Math.max(bounds.right - bounds.left, 0) * Math.max(bounds.bottom - bounds.top, 0);
};

/** Ray-cast point-in-polygon for a flat [x,y,...] ring. */
export const pointInNormalizedPolygon = (x, y, polygon) => {
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Array.isArray(polygon) ||
    polygon.length !== 8
  ) {
    return false;
  }
  let inside = false;
  for (let i = 0, j = 6; i < 8; j = i, i += 2) {
    const xi = polygon[i];
    const yi = polygon[i + 1];
    const xj = polygon[j];
    const yj = polygon[j + 1];
    const intersects =
      (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

export const normalizeAmountText = (value) => {
  const raw = String(value ?? "")
    .normalize("NFKC")
    .replace(/\u00a0/g, " ")
    .replace(/['`\s]/g, "")
    .replace(/,/g, ".")
    .replace(/[^\d.-]/g, "");
  if (!raw || raw === "-" || raw === ".") return "";
  const num = Number(raw);
  if (!Number.isFinite(num)) return "";
  return Math.abs(num).toFixed(2);
};

const amountMatchValues = (value) => {
  const normalized = normalizeAmountText(value);
  if (!normalized) return [];
  const asNumber = Number(normalized);
  const values = new Set([normalized, String(asNumber)]);
  if (Number.isInteger(asNumber)) {
    values.add(String(Math.trunc(asNumber)));
  }
  return Array.from(values).filter(Boolean);
};

const makeBbox = (polygon) =>
  polygon
    ? {
        version: 1,
        space: "normalized_page_image",
        polygon,
      }
    : null;

/**
 * Compact fallback columns to the right of an item-name box.
 * These are intentionally narrow (Anzahl / Betrag style) — PDF text snapping
 * tightens them further when a text layer is available.
 */
export const deriveAmountZonesFromNamePolygon = (namePolygon) => {
  const bounds = getPolygonBounds(namePolygon);
  if (!bounds) return { quantity: null, price: null };

  const height = Math.max(bounds.bottom - bounds.top, 0.006);
  const padY = height * 0.25;
  const top = clamp01(bounds.top - padY);
  const bottom = clamp01(bounds.bottom + padY);
  const rowLeft = clamp01(bounds.right + 0.01);
  const pageRight = 0.985;
  const available = pageRight - rowLeft;
  if (available < 0.04) return { quantity: null, price: null };

  // Rightmost compact Betrag column, then a small Anzahl column just left of it.
  const priceWidth = clamp01(Math.min(0.12, Math.max(0.07, available * 0.28)));
  const qtyWidth = clamp01(Math.min(0.08, Math.max(0.045, available * 0.16)));
  const gap = 0.012;
  const priceRight = pageRight;
  const priceLeft = clamp01(priceRight - priceWidth);
  const qtyRight = clamp01(priceLeft - gap);
  const qtyLeft = clamp01(Math.max(rowLeft, qtyRight - qtyWidth));

  const price = rectToNormalizedPolygon(priceLeft, top, priceRight, bottom);
  const quantity =
    qtyRight - qtyLeft >= 0.03
      ? rectToNormalizedPolygon(qtyLeft, top, qtyRight, bottom)
      : null;

  return { quantity, price };
};

export const priceMatchValuesForItem = (item) => {
  const values = new Set(amountMatchValues(item?.["Item Price"]));
  const qty = Number(String(item?.["Item Quantity"] ?? "").replace(",", "."));
  const price = Number(String(item?.["Item Price"] ?? "").replace(",", "."));
  if (Number.isFinite(qty) && Number.isFinite(price) && qty > 0) {
    amountMatchValues(qty * price).forEach((value) => values.add(value));
  }
  return Array.from(values);
};

/** Measure PDF.js text-layer spans as normalized boxes relative to the page surface. */
export const measureTextLayerBoxes = (textDivs, referenceEl) => {
  if (!referenceEl || !textDivs?.length) return [];
  const pageRect = referenceEl.getBoundingClientRect();
  // Prefer layout size (stable while partially scrolled) over visible viewport box.
  const width = referenceEl.offsetWidth || pageRect.width;
  const height = referenceEl.offsetHeight || pageRect.height;
  if (!width || !height) return [];

  return textDivs
    .map((div, index) => {
      if (!div) return null;
      const rect = div.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      const left = (rect.left - pageRect.left) / width;
      const top = (rect.top - pageRect.top) / height;
      const right = (rect.right - pageRect.left) / width;
      const bottom = (rect.bottom - pageRect.top) / height;
      const polygon = rectToNormalizedPolygon(left, top, right, bottom);
      if (!polygon) return null;
      const text = String(div.textContent || "");
      return {
        index,
        text,
        amount: normalizeAmountText(text),
        search: normalizeSearchText(text),
        bounds: getPolygonBounds(polygon),
        polygon,
      };
    })
    .filter(Boolean);
};

export const unionClientRectsToPolygon = (elements, referenceEl) => {
  if (!referenceEl || !elements?.length) return null;
  const pageRect = referenceEl.getBoundingClientRect();
  const width = referenceEl.offsetWidth || pageRect.width;
  const height = referenceEl.offsetHeight || pageRect.height;
  if (!width || !height) return null;

  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  let any = false;
  elements.forEach((el) => {
    if (!el?.getBoundingClientRect) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    any = true;
    left = Math.min(left, rect.left);
    top = Math.min(top, rect.top);
    right = Math.max(right, rect.right);
    bottom = Math.max(bottom, rect.bottom);
  });
  if (!any) return null;
  return rectToNormalizedPolygon(
    (left - pageRect.left) / width,
    (top - pageRect.top) / height,
    (right - pageRect.left) / width,
    (bottom - pageRect.top) / height
  );
};

const padPolygon = (polygon, padX = 0.003, padY = 0.0025) => {
  const bounds = getPolygonBounds(polygon);
  if (!bounds) return null;
  return rectToNormalizedPolygon(
    bounds.left - padX,
    bounds.top - padY,
    bounds.right + padX,
    bounds.bottom + padY
  );
};

const yOverlapRatio = (a, b) => {
  if (!a || !b) return 0;
  const overlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  if (overlap <= 0) return 0;
  const minHeight = Math.max(
    Math.min(a.bottom - a.top, b.bottom - b.top),
    0.001
  );
  return overlap / minHeight;
};

const snapTargetToPolygon = (target, polygon) => {
  const snapped = padPolygon(polygon);
  if (!snapped) return target;
  return {
    ...target,
    sourceBbox: makeBbox(snapped),
    geometrySpace: "display",
    snappedFromText: true,
  };
};

/**
 * Snap hit targets onto the rendered PDF text layer.
 * Pass 1: item names via pdf.js span matching (handles split words).
 * Pass 2: amounts near the snapped name row (OCR image boxes are not trusted for PDF).
 */
export const refineHitTargetsWithTextBoxes = (
  targets,
  textBoxes = [],
  { textItems = null, textDivs = null, referenceEl = null } = {}
) => {
  if (!targets?.length) return targets || [];

  const usedBoxIndexes = new Set();
  let next = targets.map((target) => ({ ...target }));

  // Pass 1 — snap item names from text layer / text items.
  next = next.map((target) => {
    if (target.field !== "Item Name") return target;
    const query = target.query || target.matchValues?.[0] || "";
    if (textItems && textDivs && referenceEl && query) {
      const indexes = findTextHighlightSpanIndexes(
        textItems,
        query,
        target.occurrence || 0
      );
      if (indexes.length) {
        const nodes = indexes
          .map((index) => textDivs[index])
          .filter(Boolean);
        const polygon = unionClientRectsToPolygon(nodes, referenceEl);
        if (polygon) {
          indexes.forEach((index) => usedBoxIndexes.add(index));
          return snapTargetToPolygon(target, polygon);
        }
      }
    }

    if (!textBoxes.length) return target;
    const wanted = normalizeSearchText(query);
    if (wanted.length < 3) return target;
    const candidates = textBoxes
      .map((box) => {
        if (usedBoxIndexes.has(box.index) || !box.search) return null;
        const prefix = sharedPrefixLength(box.search, wanted);
        const prefixRatio = prefix / Math.max(wanted.length, box.search.length, 1);
        const exact = box.search === wanted;
        const contains =
          box.search.includes(wanted) || wanted.includes(box.search);
        const fuzzyPrefix = prefix >= 8 && prefixRatio >= 0.55;
        if (!exact && !contains && !fuzzyPrefix) return null;
        let score = exact ? 100 : contains ? 70 : 60 + prefixRatio * 25;
        score -= polygonArea(box.polygon) * 20;
        return { box, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    const best = candidates[0];
    if (!best || best.score < 60) return target;
    usedBoxIndexes.add(best.box.index);
    return snapTargetToPolygon(target, best.box.polygon);
  });

  // Row bands from snapped names (PDF display space). Fall back to OCR only if needed.
  const nameRowBands = new Map();
  next.forEach((target) => {
    if (target.field !== "Item Name" || !target.itemKey) return;
    const bounds = getPolygonBounds(
      getValidNormalizedPolygon(target.sourceBbox)
    );
    if (bounds) nameRowBands.set(target.itemKey, bounds);
  });

  // Pass 2 — snap amounts / totals using display-space row bands.
  next = next.map((target) => {
    const isAmountField =
      target.field === "Item Quantity" ||
      target.field === "Item Price" ||
      String(target.field || "").includes("total") ||
      String(target.field || "").includes("discount");
    if (!isAmountField || !textBoxes.length) return target;

    const wantedAmounts = new Set(
      (target.matchValues || [])
        .map((value) => normalizeAmountText(value))
        .filter(Boolean)
    );
    if (!wantedAmounts.size) return target;

    const rowBand =
      (target.itemKey && nameRowBands.get(target.itemKey)) || null;
    const nameWasSnapped = next.some(
      (candidate) =>
        candidate.itemKey === target.itemKey &&
        candidate.field === "Item Name" &&
        candidate.snappedFromText
    );

    const candidates = textBoxes
      .map((box) => {
        if (usedBoxIndexes.has(box.index)) return null;
        if (!box.amount || !wantedAmounts.has(box.amount)) return null;
        let score = 100;
        if (rowBand) {
          const overlap = yOverlapRatio(rowBand, box.bounds);
          const rowCenter = (rowBand.top + rowBand.bottom) / 2;
          const boxCenter = (box.bounds.top + box.bounds.bottom) / 2;
          const yDistance = Math.abs(rowCenter - boxCenter);
          if (nameWasSnapped && overlap < 0.15 && yDistance > 0.035) {
            return null;
          }
          score += overlap * 25;
          score -= yDistance * 80;
          if (box.bounds.left + 0.005 >= rowBand.left) score += 12;
          if (target.field === "Item Quantity") {
            if (box.bounds.left > 0.9) score -= 12;
            else score += 6;
          }
          if (target.field === "Item Price") {
            score += box.bounds.left * 10;
          }
        } else {
          // No row band — prefer unique amounts; de-prioritize ambiguous ones later.
          score -= 15;
        }
        score -= polygonArea(box.polygon) * 50;
        return { box, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);

    const best = candidates[0];
    if (!best || best.score < 60) return target;
    // Ambiguous duplicate amounts on different rows without a name band.
    if (
      !rowBand &&
      candidates.length > 1 &&
      Math.abs(candidates[0].score - candidates[1].score) < 8
    ) {
      return target;
    }
    usedBoxIndexes.add(best.box.index);
    return snapTargetToPolygon(target, best.box.polygon);
  });

  return next;
};

const pushTarget = (targets, target) => {
  if (!target?.field) return;
  const polygon = getValidNormalizedPolygon(target.sourceBbox);
  const hasGeometry = !!polygon;
  const matchValues = (target.matchValues || []).filter(Boolean);
  if (!hasGeometry && !matchValues.length) return;
  targets.push({
    ...target,
    id:
      target.id ||
      [target.itemKey || "sum", target.field, target.pageNo]
        .filter((part) => part !== undefined && part !== null)
        .join(":"),
    sourceBbox: hasGeometry ? target.sourceBbox : null,
    matchValues,
    priority: Number.isFinite(target.priority) ? target.priority : 50,
  });
};

/**
 * Build reverse-navigation targets for the current preview page.
 * Geometry comes from Item.source_bbox; amount zones are derived from the
 * name box. Totals rely on amount-text matching.
 */
export const buildPreviewHitTargets = ({
  treatments = [],
  sumValues = null,
  pageNo = 1,
} = {}) => {
  const targets = [];
  const nameOccurrences = new Map();

  (treatments || []).forEach((treatment) => {
    (treatment?.Items || []).forEach((item) => {
      const query = String(item?.["Item Name"] || "").trim();
      const normalizedName = normalizeSearchText(query);
      const occurrence = nameOccurrences.get(normalizedName) || 0;
      if (normalizedName) {
        nameOccurrences.set(normalizedName, occurrence + 1);
      }

      const namePolygon = getValidNormalizedPolygon(item?.source_bbox);
      if (namePolygon) {
        pushTarget(targets, {
          itemKey: item.key,
          treatmentKey: treatment.key,
          field: "Item Name",
          pageNo,
          sourceBbox: item.source_bbox,
          query,
          occurrence,
          matchValues: [normalizedName].filter((value) => value.length >= 3),
          priority: 10,
        });

        const zones = deriveAmountZonesFromNamePolygon(namePolygon);
        pushTarget(targets, {
          itemKey: item.key,
          treatmentKey: treatment.key,
          field: "Item Quantity",
          pageNo,
          sourceBbox: makeBbox(zones.quantity),
          matchValues: amountMatchValues(item?.["Item Quantity"]),
          priority: 20,
        });
        pushTarget(targets, {
          itemKey: item.key,
          treatmentKey: treatment.key,
          field: "Item Price",
          pageNo,
          sourceBbox: makeBbox(zones.price),
          matchValues: priceMatchValuesForItem(item),
          priority: 20,
        });
      } else {
        // No geometry — still allow text/amount matching for this row.
        pushTarget(targets, {
          itemKey: item.key,
          treatmentKey: treatment.key,
          field: "Item Name",
          pageNo,
          query,
          occurrence,
          matchValues: [normalizedName].filter((value) => value.length >= 3),
          priority: 35,
        });
        pushTarget(targets, {
          itemKey: item.key,
          treatmentKey: treatment.key,
          field: "Item Quantity",
          pageNo,
          matchValues: amountMatchValues(item?.["Item Quantity"]),
          priority: 40,
        });
        pushTarget(targets, {
          itemKey: item.key,
          treatmentKey: treatment.key,
          field: "Item Price",
          pageNo,
          matchValues: priceMatchValuesForItem(item),
          priority: 40,
        });
      }
    });

    // Treatment-level text fields (no OCR boxes, text-only reverse jump).
    ["Pet Name", "Diagnose"].forEach((field) => {
      const value = normalizeSearchText(treatment?.[field]);
      if (value.length < 3) return;
      pushTarget(targets, {
        itemKey: null,
        treatmentKey: treatment.key,
        field,
        pageNo,
        matchValues: [value],
        priority: 45,
      });
    });
  });

  const sumFields = [
    ["invoice_total_sum_price_without_vat", 60],
    ["invoice_total_sum_price_with_vat", 55],
    ["total_discount_amount", 65],
    ["calculated_total_sum_manual", 70],
    ["calculated_total_sum_after_discount_manual", 70],
  ];
  sumFields.forEach(([field, priority]) => {
    pushTarget(targets, {
      itemKey: null,
      treatmentKey: null,
      field,
      pageNo,
      matchValues: amountMatchValues(sumValues?.[field]),
      priority,
    });
  });

  return targets;
};

export const displayRotationForTarget = (target, pageRotation = 0) =>
  target?.geometrySpace === "display" ? 0 : pageRotation;

export const findHitTargetAtPoint = (
  targets,
  x,
  y,
  { pageNo, rotation = 0 } = {}
) => {
  const candidates = (targets || []).filter((target) => {
    if (pageNo != null && target.pageNo != null && target.pageNo !== pageNo) {
      return false;
    }
    const polygon = getValidNormalizedPolygon(target.sourceBbox);
    if (!polygon) return false;
    const rotated = rotateNormalizedPolygon(
      polygon,
      displayRotationForTarget(target, rotation)
    );
    return pointInNormalizedPolygon(x, y, rotated);
  });
  if (!candidates.length) return null;
  candidates.sort((a, b) => {
    const areaDiff =
      polygonArea(getValidNormalizedPolygon(a.sourceBbox)) -
      polygonArea(getValidNormalizedPolygon(b.sourceBbox));
    if (areaDiff !== 0) return areaDiff;
    return a.priority - b.priority;
  });
  return candidates[0];
};

export const findHitTargetByText = (targets, rawText, { pageNo } = {}) => {
  const amount = normalizeAmountText(rawText);
  const text = normalizeSearchText(rawText);
  if (!amount && text.length < 3) return null;

  const scored = [];
  (targets || []).forEach((target) => {
    if (pageNo != null && target.pageNo != null && target.pageNo !== pageNo) {
      return;
    }
    const values = target.matchValues || [];
    if (
      amount &&
      values.some((value) => normalizeAmountText(value) === amount)
    ) {
      scored.push({ target, score: 1000 - (target.priority || 50) });
      return;
    }
    if (text.length < 3) return;
    let best = 0;
    values.forEach((value) => {
      if (!value) return;
      if (value === text) {
        best = Math.max(best, 100);
        return;
      }
      if (value.includes(text) || text.includes(value)) {
        best = Math.max(best, 80);
        return;
      }
      const prefix = sharedPrefixLength(value, text);
      const prefixRatio = prefix / Math.max(value.length, text.length, 1);
      if (prefix >= 8 && prefixRatio >= 0.55) {
        best = Math.max(best, 60 + prefixRatio * 20);
      }
    });
    if (best > 0) {
      scored.push({ target, score: best * 10 - (target.priority || 50) });
    }
  });
  if (!scored.length) return null;
  scored.sort((a, b) => b.score - a.score);
  return scored[0].target;
};
